import React from 'react';
import DrawDateBadge from '../DrawDateBadge';

/**
 * HeroSection - Reusable hero banner component
 * 
 * @param {string} title - Main heading text
 * @param {string} subtitle - Secondary text beneath title
 * @param {string} bgGradient - Tailwind gradient classes (default: purple-to-indigo)
 * @param {string} ctaLabel - CTA button text (optional)
 * @param {function} onCtaClick - CTA button click handler (optional)
 * @param {React.Component} icon - Lucide icon component (optional)
 * @param {React.ReactNode} children - Additional content (optional)
 */
export default function HeroSection({
  title,
  subtitle,
  bgGradient = 'from-purple-600 via-violet-600 to-indigo-600',
  ctaLabel,
  onCtaClick,
  icon: Icon,
  drawDate,
  children,
}) {
  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-r ${bgGradient} dark:brightness-90 text-white py-16 px-4`}
    >
      {/* Subtle overlay for depth */}
      <div className="absolute inset-0 bg-black/10" />

      {/* Decorative floating circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full animate-float-slow" />
        <div className="absolute top-32 right-20 w-24 h-24 bg-white rounded-full animate-float-medium" />
        <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-white rounded-full animate-float-fast" />
        <div className="absolute bottom-32 right-1/3 w-20 h-20 bg-white rounded-full animate-float-slow" />
      </div>

      <div className="relative max-w-7xl mx-auto text-center">
        {/* Optional icon */}
        {Icon && (
          <div className="mb-4 flex justify-center">
            <Icon className="w-12 h-12 drop-shadow-lg" />
          </div>
        )}

        {/* Title */}
        {title && (
          <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg">
            {title}
          </h1>
        )}

        {/* Subtitle */}
        {subtitle && (
          <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto drop-shadow">
            {subtitle}
          </p>
        )}

        <DrawDateBadge draw={drawDate} className="mb-8" />

        {/* CTA Button */}
        {ctaLabel && onCtaClick && (
          <button
            onClick={onCtaClick}
            className="bg-white text-purple-700 dark:text-purple-800 font-bold text-lg px-10 py-4 rounded-full hover:bg-purple-50 dark:hover:bg-gray-100 transition-all shadow-xl hover:scale-105"
          >
            {ctaLabel}
          </button>
        )}

        {/* Additional custom content */}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </section>
  );
}
